// File: frontend/src/pages/CategoryDetails.tsx
// Fixed to use refactored API and hooks with enhanced mobile-first design

import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  Eye, 
  EyeOff, 
  FolderTree, 
  Tag, 
  Calendar,
  Hash,
  Settings,
  Trash2,
  Plus,
  ExternalLink,
  RefreshCw,
  Users,
  BarChart3,
  Loader2
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { 
  useCategory, 
  useParentCategories,
  useSubcategories,
  useUpdateCategory,
  useDeleteCategory,
  useToggleCategoryStatus
} from '@/hooks/useCategories';
import { CategoryFormData } from '@/lib/api/categoryService';
import { CategoryForm } from '@/components/Category';

type ViewMode = 'view' | 'edit';

const CategoryDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('view');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const categoryId = parseInt(id || '0');

  // React Query hooks
  const { 
    data: category, 
    isLoading, 
    error, 
    refetch 
  } = useCategory(categoryId);

  const { 
    data: parentCategories 
  } = useParentCategories();

  const { 
    data: subcategories,
    isLoading: isLoadingSubcategories
  } = useSubcategories(categoryId);

  // Mutation hooks
  const updateCategoryMutation = useUpdateCategory();
  const deleteCategoryMutation = useDeleteCategory();
  const toggleStatusMutation = useToggleCategoryStatus();

  // Event handlers
  const handleEdit = useCallback(() => {
    setViewMode('edit');
  }, []);

  const handleCancelEdit = useCallback(() => {
    setViewMode('view');
  }, []);

  const handleUpdateCategory = useCallback(async (formData: CategoryFormData) => {
    try {
      await updateCategoryMutation.mutateAsync({
        id: categoryId,
        data: formData
      });
      toast.success('Category updated successfully');
      setViewMode('view');
      refetch();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update category');
      throw error;
    }
  }, [categoryId, updateCategoryMutation, refetch]);

  const handleToggleStatus = useCallback(async () => {
    try {
      await toggleStatusMutation.mutateAsync(categoryId);
      toast.success('Category status updated successfully');
      refetch();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update category status');
    }
  }, [categoryId, toggleStatusMutation, refetch]);

  const handleDeleteCategory = useCallback(async () => {
    if (!category) return;

    try {
      await deleteCategoryMutation.mutateAsync(categoryId);
      toast.success('Category deleted successfully');
      navigate('/categories');
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete category');
    }
  }, [categoryId, deleteCategoryMutation, navigate, category]);

  const handleGoBack = useCallback(() => {
    navigate('/categories');
  }, [navigate]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading category details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !category) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <FolderTree className="h-12 w-12 mx-auto mb-2" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Category not found</h3>
          <p className="text-gray-600 mb-4">
            The category you're looking for doesn't exist or has been deleted.
          </p>
          <button 
            onClick={handleGoBack} 
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Categories
          </button>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
        <Eye className="h-4 w-4 mr-1" />
        Active
      </span>
    ) : (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
        <EyeOff className="h-4 w-4 mr-1" />
        Inactive
      </span>
    );
  };

  const getParentCategoryName = () => {
    if (!category.parent_category_id) return 'Top Level Category';
    const parent = parentCategories?.find((p:any) => p.category_id === category.parent_category_id);
    return parent?.name || 'Unknown Parent';
  };

  const isLoading_Operations = updateCategoryMutation.isPending || 
                              deleteCategoryMutation.isPending || 
                              toggleStatusMutation.isPending;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-4 py-6 sm:px-6 lg:px-8 lg:py-8 max-w-7xl mx-auto">
        
        {/* View Mode */}
        {viewMode === 'view' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <button
                  onClick={handleGoBack}
                  className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                    {category.icon && (
                      <i className={`${category.icon} text-blue-600 mr-3`}></i>
                    )}
                    {category.name}
                  </h1>
                  <p className="text-sm text-gray-600 mt-1">
                    Category details and management
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleToggleStatus}
                  disabled={isLoading_Operations}
                  className={`inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium ${
                    category.is_active 
                      ? 'text-red-700 hover:bg-red-50' 
                      : 'text-green-700 hover:bg-green-50'
                  } bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {toggleStatusMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : category.is_active ? (
                    <EyeOff className="h-4 w-4 mr-2" />
                  ) : (
                    <Eye className="h-4 w-4 mr-2" />
                  )}
                  {category.is_active ? 'Deactivate' : 'Activate'}
                </button>
                
                <button
                  onClick={handleEdit}
                  disabled={isLoading_Operations}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Category
                </button>
              </div>
            </div>

            {/* Category Information Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Information Card */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                      <Tag className="h-5 w-5 mr-2 text-gray-500" />
                      Category Information
                    </h3>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Category Name
                        </label>
                        <p className="text-sm text-gray-900">{category.name}</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Status
                        </label>
                        {getStatusBadge(category.is_active || false)}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Parent Category
                        </label>
                        <p className="text-sm text-gray-900">{getParentCategoryName()}</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Display Order
                        </label>
                        <p className="text-sm text-gray-900 flex items-center">
                          <Hash className="h-4 w-4 mr-1 text-gray-400" />
                          {category.display_order || 0}
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Icon Class
                        </label>
                        <p className="text-sm text-gray-900 flex items-center">
                          {category.icon ? (
                            <>
                              <i className={`${category.icon} mr-2`}></i>
                              {category.icon}
                            </>
                          ) : (
                            <span className="text-gray-500">No icon set</span>
                          )}
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Subcategories
                        </label>
                        <p className="text-sm text-gray-900 flex items-center">
                          <FolderTree className="h-4 w-4 mr-1 text-gray-400" />
                          {category.subcategory_count || 0}
                        </p>
                      </div>
                    </div>
                    
                    {category.description && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <p className="text-sm text-gray-900">{category.description}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Quick Stats */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                      <BarChart3 className="h-5 w-5 mr-2 text-gray-500" />
                      Quick Stats
                    </h3>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Created</span>
                      <span className="text-sm font-medium text-gray-900 flex items-center">
                        <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                        {category.created_at ? formatDate(category.created_at) : 'N/A'}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Last Updated</span>
                      <span className="text-sm font-medium text-gray-900 flex items-center">
                        <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                        {category.updated_at ? formatDate(category.updated_at) : 'N/A'}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Category Type</span>
                      <span className="text-sm font-medium text-gray-900">
                        {category.parent_category_id ? 'Sub-category' : 'Parent Category'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                      <Settings className="h-5 w-5 mr-2 text-gray-500" />
                      Quick Actions
                    </h3>
                  </div>
                  <div className="p-6 space-y-3">
                    <button
                      onClick={() => navigate(`/categories?parent=${categoryId}`)}
                      className="w-full flex items-center justify-between p-3 text-left text-sm font-medium text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <span className="flex items-center">
                        <FolderTree className="h-4 w-4 mr-2" />
                        View Subcategories
                      </span>
                      <ExternalLink className="h-4 w-4" />
                    </button>
                    
                    <button
                      onClick={() => navigate(`/service-types?category=${categoryId}`)}
                      className="w-full flex items-center justify-between p-3 text-left text-sm font-medium text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <span className="flex items-center">
                        <Tag className="h-4 w-4 mr-2" />
                        View Service Types
                      </span>
                      <ExternalLink className="h-4 w-4" />
                    </button>
                    
                    <button
                      onClick={() => navigate('/categories')}
                      className="w-full flex items-center justify-between p-3 text-left text-sm font-medium text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <span className="flex items-center">
                        <FolderTree className="h-4 w-4 mr-2" />
                        All Categories
                      </span>
                      <ExternalLink className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Danger Zone */}
                <div className="bg-white rounded-lg shadow-sm border border-red-200">
                  <div className="px-6 py-4 border-b border-red-200">
                    <h3 className="text-lg font-medium text-red-900 flex items-center">
                      <Trash2 className="h-5 w-5 mr-2 text-red-500" />
                      Danger Zone
                    </h3>
                  </div>
                  <div className="p-6">
                    <p className="text-sm text-gray-600 mb-4">
                      Delete this category permanently. This action cannot be undone.
                    </p>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={isLoading_Operations}
                      className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Category
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Subcategories Section */}
            {(category.subcategory_count || 0) > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                      <FolderTree className="h-5 w-5 mr-2 text-gray-500" />
                      Subcategories ({category.subcategory_count})
                    </h3>
                    <button
                      onClick={() => navigate(`/categories?parent=${categoryId}`)}
                      className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-600 hover:text-blue-700"
                    >
                      View All
                      <ExternalLink className="h-4 w-4 ml-1" />
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  {isLoadingSubcategories ? (
                    <div className="text-center py-4">
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <p className="text-gray-600 mt-2">Loading subcategories...</p>
                    </div>
                  ) : subcategories && subcategories.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {subcategories.slice(0, 6).map((subcat: any) => (
                        <div key={subcat.category_id} className="flex items-center p-3 bg-gray-50 rounded-lg">
                          {subcat.icon && (
                            <i className={`${subcat.icon} text-gray-500 mr-3`}></i>
                          )}
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-900">{subcat.name}</h4>
                            <p className="text-xs text-gray-500">
                              {subcat.is_active ? 'Active' : 'Inactive'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No subcategories found</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Edit Mode */}
        {viewMode === 'edit' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <button
                  onClick={handleCancelEdit}
                  className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">Edit Category</h1>
                  <p className="text-sm text-gray-600">Update category information and settings</p>
                </div>
              </div>
            </div>

            <CategoryForm
              category={category}
              parentCategories={parentCategories || []}
              onSubmit={handleUpdateCategory}
              onCancel={handleCancelEdit}
              isSubmitting={updateCategoryMutation.isPending}
            />
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="bg-red-100 rounded-full p-3 mr-4">
                    <Trash2 className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Delete Category</h3>
                    <p className="text-sm text-gray-600">This action cannot be undone</p>
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 mb-6">
                  Are you sure you want to delete "<strong>{category.name}</strong>"? 
                  This will permanently remove the category and all its data.
                </p>
                
                <div className="flex items-center justify-end space-x-3">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={deleteCategoryMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteCategory}
                    disabled={deleteCategoryMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
                  >
                    {deleteCategoryMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Category
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryDetails;